/**
 * Assign Route Modal Component
 * Modal for assigning a driver to a route
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useAssignRoute } from '../../../hooks/useDrivers';
import { useVehicles } from '../../../hooks/useVehicles';
import { assignRouteSchema } from '../../../schemas/driverSchema';
import type { AssignRouteFormData } from '../../../schemas/driverSchema';

interface AssignRouteModalProps {
  driverId: string;
  open: boolean;
  onClose: () => void;
}

export function AssignRouteModal({ driverId, open, onClose }: AssignRouteModalProps) {
  const { mutateAsync: assignRoute, isPending } = useAssignRoute();
  const { data: vehiclesData } = useVehicles({ status: 'available' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<AssignRouteFormData>({
    routeId: '',
    routeName: '',
    vehicleId: '',
  });

  const availableVehicles = vehiclesData?.vehicles.filter((v) => v.dotCompliance.isCompliant) || [];

  useEffect(() => {
    if (open) {
      setFormData({ routeId: '', routeName: '', vehicleId: '' });
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = assignRouteSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) newErrors[err.path[0] as string] = err.message;
      });
      setErrors(newErrors);
      return;
    }

    try {
      await assignRoute({ driverId, data: result.data });
      onClose();
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to assign route' });
    }
  };

  const updateField = (field: keyof AssignRouteFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Route</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Route ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.routeId}
              onChange={(e) => updateField('routeId', e.target.value)}
              className={`mt-1 block w-full rounded-md border ${
                errors.routeId ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="ROUTE-001"
            />
            {errors.routeId && <p className="mt-1 text-sm text-red-500">{errors.routeId}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Route Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.routeName}
              onChange={(e) => updateField('routeName', e.target.value)}
              className={`mt-1 block w-full rounded-md border ${
                errors.routeName ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="North County Route"
            />
            {errors.routeName && <p className="mt-1 text-sm text-red-500">{errors.routeName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Vehicle <span className="text-red-500">*</span>
            </label>
            {availableVehicles.length === 0 ? (
              <p className="mt-1 text-sm text-gray-500">No available vehicles with DOT compliance</p>
            ) : (
              <select
                value={formData.vehicleId}
                onChange={(e) => updateField('vehicleId', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.vehicleId ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              >
                <option value="">Select a vehicle</option>
                {availableVehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.vehicleNumber} - {vehicle.make} {vehicle.model}
                  </option>
                ))}
              </select>
            )}
            {errors.vehicleId && <p className="mt-1 text-sm text-red-500">{errors.vehicleId}</p>}
          </div>

          {errors.submit && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errors.submit}</div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || availableVehicles.length === 0}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                'Assign Route'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
